import type { BoardDocument, DiagramCheckpoint, DiagramSyncCheckpoint } from "../types/board";
import { canonicalDocument, decodeCheckpoint, persistableSnapshot } from "./board-document";
import { diagramRevisionSchema } from "../validation/diagram-revision";
import { expandGroupSelection } from "./canvas-groups";

export function compareRevision(left: string, right: string): number {
  const a = BigInt(diagramRevisionSchema.parse(left)), b = BigInt(diagramRevisionSchema.parse(right));
  return a < b ? -1 : a > b ? 1 : 0;
}

export function collaborationCheckpoint(diagramId: string, value: DiagramSyncCheckpoint): DiagramCheckpoint {
  if (!value || value.diagram_id !== diagramId) throw new Error("Unexpected diagram checkpoint");
  return decodeCheckpoint(value);
}

export type Reconciliation = "older" | "duplicate" | "inconsistent" | "buffer" | "preview" | "conflict" | "adopt" | "acknowledge";
export function reconcileCheckpoint(input: {
  accepted: DiagramCheckpoint | null; incoming: DiagramCheckpoint; working: BoardDocument;
  operation?: boolean; preview?: boolean; activeSession?: boolean; submitted?: DiagramCheckpoint | null;
}): Reconciliation {
  const { accepted, incoming, working, operation, preview, activeSession, submitted } = input;
  if (accepted && compareRevision(incoming.revision, accepted.revision) < 0) return "older";
  if (operation) return "buffer";
  if (preview) return "preview";
  const content = canonicalDocument(incoming.document);
  if (accepted && compareRevision(incoming.revision, accepted.revision) === 0)
    return content === canonicalDocument(accepted.document) ? "duplicate" : "inconsistent";
  // A timed-out write may already be durable; acknowledge only its exact submitted content.
  if (submitted && content === canonicalDocument(submitted.document)) return "acknowledge";
  if (accepted && (activeSession || canonicalDocument(working) !== canonicalDocument(accepted.document))) return "conflict";
  return "adopt";
}

export function higherCheckpoint<T extends { revision: string }>(current: T | null, next: T): T {
  return current && compareRevision(current.revision, next.revision) > 0 ? current : next;
}

/** Retain personal selection only for surviving ids; never import remote measurements or view state. */
export function preserveCollaborationView(document: BoardDocument, working: BoardDocument): BoardDocument {
  const snapshot = persistableSnapshot(document.snapshot);
  const selectedNodes = new Set(working.snapshot.nodes.filter(n => n.selected).map(n => n.id));
  const selectedEdges = new Set(working.snapshot.edges.filter(e => e.selected).map(e => e.id));
  const selection = expandGroupSelection(snapshot.nodes, selectedNodes);
  snapshot.nodes = snapshot.nodes.map(node => ({ ...node, selected: selection.has(node.id) }));
  snapshot.edges = snapshot.edges.map(edge => ({ ...edge, selected: selectedEdges.has(edge.id) }));
  if (working.snapshot.viewport) snapshot.viewport = { ...working.snapshot.viewport };
  else delete snapshot.viewport;
  return { title: document.title, snapshot };
}

export interface RecoveryDocument { id: string; baseRevision: string; document: BoardDocument }
export function retainRecovery(copies: readonly RecoveryDocument[], base: string, document: BoardDocument): RecoveryDocument[] {
  const canonical = canonicalDocument(document);
  if (copies.some(copy => copy.baseRevision === base && canonicalDocument(copy.document) === canonical)) return [...copies];
  return [...copies, { id: crypto.randomUUID(), baseRevision: base,
    document: structuredClone({ title: document.title, snapshot: persistableSnapshot(document.snapshot) }) }];
}
